import React, { useCallback, useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate, useLocation } from "react-router-dom";
import {
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Stack,
  Chip,
  Divider,
} from "@mui/material";
import authApi from "../api/authApi";
import GoogleAuthButton from "../components/auth/GoogleAuthButton";

const getAuthErrorMessage = (err, fallback) => {
  const data = err?.data || err?.response?.data;
  if (data?.message) return data.message;
  if (Array.isArray(data?.errors) && data.errors[0]?.msg) return data.errors[0].msg;
  return fallback;
};

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({
    username: "",
    password: "",
  });
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState(location.state?.message || null);
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setApiError(null);
    
    const nextErrors = {};
    if (!formData.username || formData.username.length < 3) {
      nextErrors.username = "Username is required";
    }
    if (!formData.password || formData.password.length < 8) {
      nextErrors.password = "Password must be at least 8 characters";
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;
    
    setIsLoading(true);
    
    try {
      const data = await authApi.login({
        username: formData.username,
        password: formData.password,
      });

      if (data.errors) {
        const backendErrors = {};
        data.errors.forEach((err) => {
          backendErrors[err.param] = err.msg;
        });
        setErrors(backendErrors);
        return;
      }

      localStorage.setItem("token", data.token);
      navigate("/app/dashboard");
    } catch (err) {
      const data = err?.data || err?.response?.data;
      if (Array.isArray(data?.errors)) {
        const backendErrors = {};
        data.errors.forEach((error) => {
          backendErrors[error.param] = error.msg;
        });
        setErrors(backendErrors);
      }
      setApiError(getAuthErrorMessage(err, "Sign in failed. Please try again."));
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleCredential = useCallback(
    async (credential) => {
      setApiError(null);
      setErrors({});
      setIsLoading(true);

      try {
        const data = await authApi.googleLogin({ credential });
        localStorage.setItem("token", data.token);
        navigate("/app/dashboard");
      } catch (err) {
        setApiError(
          getAuthErrorMessage(err, "Google sign in failed. Please try again.")
        );
      } finally {
        setIsLoading(false);
      }
    },
    [navigate]
  );

  return (
    <>
      <Helmet>
        <title>Sign In | CaloriesPal</title>
        <meta
          name="description"
          content="Login securely to CaloriesPal to manage recipes, build nutrition labels, and access ingredient intelligence."
        />
        <link rel="canonical" href="https://www.caloriespal.com/login" />
      </Helmet>
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: { xs: 2, md: 4 },
          py: { xs: 6, md: 10 },
          background: "linear-gradient(135deg, #0f172a 0%, #0f172a 55%, #a3e635 100%)",
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "minmax(0,1fr) 540px" },
            maxWidth: "min(1280px, 100%)",
            width: "100%",
            gap: { xs: 4, md: 0 },
            borderRadius: 4,
            overflow: "hidden",
            boxShadow: "0 30px 80px rgba(15,23,42,0.45)",
            background: "rgba(15,23,42,0.35)",
            backdropFilter: "blur(10px)",
            mx: { xs: 0, lg: 4 },
          }}
        >
          <Box
            sx={{
              color: "white",
              p: { xs: 3, md: 6, lg: 8 },
              display: "flex",
              flexDirection: "column",
              gap: 3,
              minHeight: { xs: "auto", lg: "100%" },
            }}
          >
            <Chip label="Returning creators" color="success" sx={{ width: "fit-content" }} />
            <Typography variant="h3" fontWeight={700}>
              Welcome back to your nutrition lab.
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.75)" }}>
              Resume where you left off: tweak recipes, export labels, or check your live
              ingredient insights—all from one secure dashboard.
            </Typography>
            <Stack spacing={2}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <span className="text-xl">🛡</span>
                <Typography>Encrypted login with instant verification checks.</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <span className="text-xl">⚡</span>
                <Typography>Syncs every board, recipe, and ingredient immediately.</Typography>
              </Box>
            </Stack>
          </Box>
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              backgroundColor: "white",
              borderRadius: { xs: "32px 32px 0 0", lg: 0 },
              p: { xs: 4, md: 6 },
              display: "flex",
              flexDirection: "column",
              gap: 2,
              justifyContent: "center",
              minHeight: { xs: "auto", lg: "100%" },
              width: "100%",
            }}
          >
            <Button
              size="small"
              onClick={() => navigate("/")}
              sx={{ alignSelf: "flex-start", mb: 2, textTransform: "none" }}
            >
              ← Back to Home
            </Button>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
              Sign in
            </Typography>
            <GoogleAuthButton
              label="signin_with"
              onCredential={handleGoogleCredential}
              disabled={isLoading}
            />
            <Divider sx={{ my: 1 }}>or sign in with username</Divider>

            {apiError && (
              <Alert severity={location.state?.message ? "success" : "error"}>
                {apiError}
              </Alert>
            )}

            <TextField
              label="Username"
              name="username"
              value={formData.username}
              onChange={handleChange}
              error={!!errors.username}
              helperText={errors.username}
              fullWidth
              margin="normal"
              required
            />

            <TextField
              label="Password"
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              error={!!errors.password}
              helperText={errors.password}
              fullWidth
              margin="normal"
              required
            />
            <Button
              size="small"
              onClick={() => navigate("/forgot-password")}
              sx={{ alignSelf: "flex-end", textTransform: "none" }}
            >
              Forgot password?
            </Button>

            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={isLoading}
              sx={{
                mt: 2,
                background: "linear-gradient(135deg,#84cc16,#22d3ee)",
                boxShadow: "0 20px 40px rgba(34,211,238,0.35)",
              }}
            >
              {isLoading ? "Signing in..." : "Sign In"}
            </Button>

            <Typography variant="body2" align="center" sx={{ mt: 2 }}>
              New to CaloriesPal?{" "}
              <Button onClick={() => navigate("/signup")} size="small">
                Create an account
              </Button>
            </Typography>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              justifyContent="center"
              sx={{ mt: 1 }}
            >
              <Button
                size="small"
                onClick={() => navigate("/")}
                sx={{ textTransform: "none" }}
              >
                Back to Home
              </Button>
              <Button
                size="small"
                onClick={() => navigate("/foods")}
                sx={{ textTransform: "none" }}
              >
                Browse Foods
              </Button>
              <Button
                size="small"
                onClick={() => navigate("/signup")}
                sx={{ textTransform: "none" }}
              >
                Sign Up
              </Button>
            </Stack>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default LoginPage;
